import { useMemo } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'

import { yakurekiPollKey } from '@/apps/latest/shared/recording-status/usePollEvent'
import { usePharmacistsQuery, useSessionPharmacist } from '@/features/auth'
import { useCreatePharmacistMutation } from '@/features/internal/pharmacist'
import { useToast } from '@/shared/contexts/ToastContext'
import { queryKeys } from '@/shared/hooks/queries/queryKeys'

import { patchNutritionRecordStaff } from './api'
import { nutritionReportKey } from './useNutritionReport'

// アカウントの発行で名簿に付く既定の 1 行 (back users/models.py の DEFAULT_PHARMACIST_NAME)。人ではないので
// 担当の選択肢に出さない (design-book ADR-0078)。
const DEFAULT_ROW_NAME = 'デフォルト薬剤師'

// 担当 (署名の管理栄養士) の選択肢 = 店の名簿 (薬剤師と同じ Pharmacist。hub log/121 決定 25) と、その PC で前に
// 選んだ担当 (localStorage。1 人 1 台の PC なので、ふだんは記録を作った時点で決まっている)。
export const useNutritionStaff = () => {
  const { pharmacists } = usePharmacistsQuery()
  const staff = useMemo(
    () =>
      (pharmacists?.pharmacists ?? []).filter(
        (p) => p.name !== DEFAULT_ROW_NAME
      ),
    [pharmacists]
  )
  const { sessionPharmacistId, setSessionPharmacistId } =
    useSessionPharmacist(staff)
  // 記録に渡すのは名簿にいる人だけ。localStorage の id は名簿が空・読み込み中・別の店のアカウントのときも残り
  // (useSessionPharmacist は名簿が 1 人以上のときだけ先頭に置き換える)、そのまま送ると back が 400 で返して
  // 記録を作れない。渡さなければ担当の無い記録になり、署名はアカウントの姓名・名前の行の pill から選べる
  const sessionStaffId = staff.some((p) => p.id === sessionPharmacistId)
    ? sessionPharmacistId
    : ''
  return {
    staff,
    sessionStaffId,
    setSessionStaffId: setSessionPharmacistId,
  }
}

// 記録の担当を替える。署名は報告書の応答 (stamp) と共有の event (pharmacist_id) の両方に出るので、両方を取り直す。
export const useSetNutritionRecordStaff = (eventId: string) => {
  const queryClient = useQueryClient()
  const { showToast } = useToast()
  return useMutation({
    mutationFn: (pharmacistId: string | null) =>
      patchNutritionRecordStaff(eventId, pharmacistId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: yakurekiPollKey(eventId) })
      void queryClient.invalidateQueries({
        queryKey: nutritionReportKey(eventId),
      })
    },
    onError: () => showToast('担当を保存できませんでした', 'error'),
  })
}

// 名簿に 1 人足す (薬剤師の名簿の画面と同じ口。名前と表示名は同じ字)。名簿を取り直し終えてから id を返す
// (取り直す前に選ぶと、その PC の担当 (useSessionPharmacist) が名簿に無い id を名簿の先頭に戻す)。
// 足せなければ toast を出して投げる (呼び出し側の menu は打った名前を残したまま開いておく)。
export const useAddNutritionStaff = () => {
  const create = useCreatePharmacistMutation()
  const queryClient = useQueryClient()
  const { showToast } = useToast()
  return async (name: string): Promise<string> => {
    try {
      const created = await create.mutateAsync({ name, display_name: name })
      await queryClient.invalidateQueries({
        queryKey: queryKeys.pharmacy.pharmacists(),
      })
      return created.id
    } catch (e) {
      showToast(
        '担当者を追加できませんでした (同じ名前がもう名簿にあるかもしれません)',
        'error'
      )
      throw e
    }
  }
}
